/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  todos: {
    all: ["todos"] as const,
    list: <T extends object>(options: T) => ["todos", "list", options] as const,
    /** Paginated variant of `list` (incremental view limit). */
    listPaged: <T extends object>(options: T, limit: number) =>
      ["todos", "list", options, limit] as const,
    /** Pending-task count for a list (badge). Derived from `list` → invalidated by its prefix. */
    listCount: <T extends object>(options: T) => ["todos", "list", options, "count"] as const,
    /**
     * Task detail. Account-AGNOSTIC key: account scoping is
     * transparently injected by `createAccountPowerSyncQuery` (prefix
     * `["acct", userId, …]`). DO NOT add `userId` here — use the wrapper.
     */
    detail: (id: string | undefined) => ["todos", "detail", id] as const,
    /** Todo list_id resolution for the /todo/:id redirect. */
    detailRedirect: (id: string | undefined) => ["todos", "detail-redirect", id] as const,
    stats: (userId: string | undefined) => ["todos", "stats", userId] as const,
    remindersPool: () => ["todos", "reminders-pool"] as const,
  },
  activity: {
    all: ["activity"] as const,
    /** Local feed (activity_recent). Includes userId: isolates cache between accounts. */
    feed: (userId: string | undefined) => ["activity", "feed", userId] as const,
    /** Local history for a specific entity. */
    entity: (userId: string | undefined, entityType: string, entityId: string | undefined) =>
      ["activity", "entity", userId, entityType, entityId] as const,
  },
  trash: {
    all: ["trash"] as const,
    lists: (userId: string | undefined) => ["trash", "lists", userId] as const,
    todos: (userId: string | undefined) => ["trash", "todos", userId] as const,
  },
  search: {
    all: ["search"] as const,
    /** Search results. Account-scoped via `createAccountPowerSyncQuery`. */
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Upload queue stats (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Is there local data? (profile cleanup button). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  lists: {
    all: ["lists"] as const,
    /** Organization (sharing) associated with a list. Scoped via the account wrapper. */
    organization: (listId: string | undefined) => ["lists", "organization", listId] as const,
    /** All lists with theme + group metadata for a user (sidebar / route). */
    customLists: (userId: string | undefined) => ["lists", "customLists", userId] as const,
    /** List groups for a user. */
    groups: (userId: string | undefined) => ["lists", "groups", userId] as const,
    /** Single list detail by id. Account-scoped via `createAccountPowerSyncQuery`. */
    detail: (listId: string | undefined) => ["lists", "detail", listId] as const,
    /** Pending todo count per list_id (GROUP BY). */
    todoCounts: (userId: string | undefined) => ["lists", "todoCounts", userId] as const,
    /** Count of lists owned by user (profile stats). */
    count: (userId: string | undefined) => ["lists", "count", userId] as const,
    withCounts: (userId: string | undefined, limit?: number) =>
      ["lists", "withCounts", userId, limit] as const,
  },
  myDay: {
    all: ["myDay"] as const,
    /** "My day" tasks. Account-scoped via `createAccountPowerSyncQuery`. */
    todos: (dateKey: string) => ["myDay", "todos", dateKey] as const,
    /** "My day" suggestions. Account-scoped via the wrapper. */
    suggestions: (dateKey: string) => ["myDay", "suggestions", dateKey] as const,
  },
  smartViews: {
    counts: (userId: string | undefined) => ["smartViews", "counts", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (unpaginated getThemesQuery; profile + list-dialog SHARE cache). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated).*/
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user.*/
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id).*/
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme).*/
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  subtasks: {
    all: ["subtasks"] as const,
    /** Subtasks of a task. Account-scoped via `createAccountPowerSyncQuery`. */
    byTodo: (todoId: string | undefined) => ["subtasks", todoId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
    /** Attachments of a task. Account-scoped via `createAccountPowerSyncQuery`. */
    byTodo: (todoId: string) => ["attachments", todoId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences).*/
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector).*/
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector).*/
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all?*/
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync).*/
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists.*/
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters.*/
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    /**
     * Active sessions from the profile (device list).
     *
     * P23/CC-60 · P25: it was an INLINE array in `sessions-section`, so
     * `BootManager` had no way to invalidate it after revocation and the list
     * kept showing an already closed device. `activeSessionsAll` is the
     * prefix used for invalidation without knowing the current session id.
     */
    activeSessionsAll: ["auth", "activeSessions"] as const,
    activeSessions: (sessionId: string | undefined) =>
      ["auth", "activeSessions", sessionId] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
